/**
 * A primeira conversa. Não é cadastro: é a Ruth se apresentando.
 *
 * Por isso o botão de gravar tem 84px e fica no meio da tela, e o campo de
 * texto é o "ou escreve" embaixo dele. Responder tudo digitando com um bebê
 * no colo não acontece — a voz é o caminho principal, não a alternativa.
 *
 * A exceção é a pergunta que chega com `botoes`. Ela se responde com um toque,
 * e o toque toma o lugar do microfone e do campo. Existe por causa do
 * consentimento para guardar remédio das crianças: um "sim" que dependesse de
 * um modelo interpretar a frase não seria consentimento. O rótulo tocado vai
 * ao backend pelo mesmo caminho do texto — para ele não existe "botão", existe
 * a mãe respondendo.
 *
 * Quantas perguntas são, só a API sabe: algumas dependem da casa (a do bebê,
 * a do consentimento). Nenhum total está escrito aqui, nem no CSS.
 *
 * Não há barra de navegação aqui de propósito: enquanto a Ruth não conhece a
 * casa, não existe "Hoje" para mostrar.
 *
 * Medidas de docs/design/Onboarding.dc.html.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { api, fraseDoErro, type PassoDoOnboarding } from '../api'
import { Icone } from '../componentes/Icone'
import '../estilos/onboarding.css'

type Props = {
  /** A conversa acabou: quem monta o app troca de tela. */
  aoConcluir: () => void
}

const SEM_MICROFONE = 'Não consegui usar o microfone. Escreve pra mim?'

export function Onboarding({ aoConcluir }: Props) {
  const [passo, setPasso] = useState<PassoDoOnboarding | null>(null)
  const [rascunho, setRascunho] = useState('')
  const [pensando, setPensando] = useState(false)
  const [gravando, setGravando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const linha = useRef<HTMLDivElement>(null)
  const gravador = useRef<MediaRecorder | null>(null)
  // A trava do toque. O `pensando` desabilita o botão, mas só no próximo
  // render: dois toques no mesmo instante passariam os dois. Na pergunta do
  // consentimento, duas respostas não é detalhe — a trava fecha na hora.
  const tocando = useRef(false)

  const receber = useCallback(
    (novo: PassoDoOnboarding) => {
      setPasso(novo)
      if (novo.concluido) aoConcluir()
    },
    [aoConcluir],
  )

  useEffect(() => {
    api
      .onboarding()
      .then(receber)
      .catch((e) => setErro(fraseDoErro(e, 'não consegui abrir a conversa')))
  }, [receber])

  useEffect(() => {
    const alvo = linha.current
    if (alvo) alvo.scrollTop = alvo.scrollHeight
  }, [passo, pensando])

  const mandar = useCallback(
    async (envio: () => Promise<PassoDoOnboarding>) => {
      setPensando(true)
      setErro(null)
      try {
        receber(await envio())
        return true
      } catch (e) {
        setErro(fraseDoErro(e, 'não consegui mandar'))
        return false
      } finally {
        setPensando(false)
      }
    },
    [receber],
  )

  const escrever = useCallback(
    async (texto: string) => {
      const limpo = texto.trim()
      if (!limpo || pensando) return
      setRascunho('')
      if (!(await mandar(() => api.responderOnboarding(limpo)))) setRascunho(texto)
    },
    [mandar, pensando],
  )

  /** O rótulo vai inteiro, como veio da API: é ele que o backend espera ler. */
  const escolher = useCallback(
    async (rotulo: string) => {
      if (tocando.current || pensando) return
      tocando.current = true
      try {
        await mandar(() => api.responderOnboarding(rotulo))
      } finally {
        tocando.current = false
      }
    },
    [mandar, pensando],
  )

  const gravar = useCallback(async () => {
    if (gravando) {
      gravador.current?.stop()
      setGravando(false)
      return
    }
    try {
      const trilha = await navigator.mediaDevices.getUserMedia({ audio: true })
      const rec = new MediaRecorder(trilha)
      const pedacos: Blob[] = []
      rec.ondataavailable = (evento) => pedacos.push(evento.data)
      rec.onstop = () => {
        trilha.getTracks().forEach((t) => t.stop())
        void mandar(() => api.responderFalando(new Blob(pedacos, { type: rec.mimeType })))
      }
      rec.start()
      gravador.current = rec
      setGravando(true)
    } catch {
      setErro(SEM_MICROFONE)
    }
  }, [gravando, mandar])

  // O `?? []` é para o backend que ainda responde sem o campo: a primeira
  // conversa não pode cair por um deploy fora de ordem.
  const botoes = passo?.botoes ?? []

  return (
    <div className="onb">
      <header className="onb__cabeca">
        <Barra passo={passo} />
        <span className="onb__contagem">
          {passo ? `${passo.titulo} · ${passo.numero} de ${passo.total}` : ' '}
        </span>
      </header>

      <div className="onb__linha" ref={linha}>
        {passo?.mensagens.map((fala) => (
          <div
            key={fala.id}
            className={`balao ${fala.de === 'mae' ? 'balao--mae' : 'balao--ruth'}`}
          >
            {fala.texto}
          </div>
        ))}

        {pensando && (
          <div className="balao balao--ruth balao--pensando" aria-label="A Ruth está escrevendo">
            <span />
            <span />
            <span />
          </div>
        )}

        {erro && <p className="onb__erro">{erro}</p>}
      </div>

      <div className="onb__voz">
        {botoes.length > 0 ? (
          <div className="onb__escolhas">
            {botoes.map((rotulo) => (
              <button
                key={rotulo}
                type="button"
                className="onb__escolha"
                disabled={pensando}
                onClick={() => void escolher(rotulo)}
              >
                {rotulo}
              </button>
            ))}
          </div>
        ) : (
          <>
            <button
              type="button"
              className={`onb__botao ${gravando ? 'onb__botao--gravando' : ''}`}
              aria-label={gravando ? 'Parar e mandar' : 'Falar com a Ruth'}
              disabled={pensando}
              onClick={() => void gravar()}
            >
              <Icone nome={gravando ? 'parar' : 'microfone'} tamanho={36} />
            </button>

            <form
              className="onb__escrever"
              onSubmit={(evento) => {
                evento.preventDefault()
                void escrever(rascunho)
              }}
            >
              <input
                className="onb__campo"
                value={rascunho}
                onChange={(evento) => setRascunho(evento.target.value)}
                placeholder={gravando ? 'Gravando…' : 'ou escreve aqui'}
                aria-label="Escreva sua resposta"
                disabled={gravando}
              />
              <button
                type="submit"
                className="onb__mandar"
                aria-label="Mandar"
                disabled={pensando || !rascunho.trim()}
              >
                <Icone nome="enviar" tamanho={20} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

/** Um tracinho por pergunta. A mãe vê que tem fim — e que o fim está perto. */
function Barra({ passo }: { passo: PassoDoOnboarding | null }) {
  // Antes da API responder, a barra é só o espaço dela. Quantos tracinhos são
  // não é nosso para chutar — e um chute errado faria a barra pular quando a
  // resposta chegasse.
  if (!passo) return <div className="onb__barra" aria-hidden="true" />

  return (
    <div
      className="onb__barra"
      role="progressbar"
      aria-valuenow={passo.numero}
      aria-valuemax={passo.total}
    >
      {Array.from({ length: passo.total }, (_, i) => (
        <span key={i} className={`onb__traco ${i < passo.numero ? 'onb__traco--feito' : ''}`} />
      ))}
    </div>
  )
}
